import { getCouponBadge } from "./coupon-badge";
import { formatMoney, parseDiscountAmounts, parseMoney, parseMoneyAll } from "./discount-parse";

export type OfferDiscountType = "PERCENT" | "FIXED" | "FREE_SHIPPING" | "DEAL" | null | undefined;

export interface OfferDiscountDisplay {
    value: string;
    suffix: string;
    isPercent: boolean;
}

/**
 * The headline figure shown on a compact offer card (CouponGridCard). Same rule as the store-page coupon card
 * (src/lib/coupon-badge.ts), so the list and the card never show different figures for one coupon: the figure never
 * contradicts the offer text (description, else title). Returns null when the offer has no clear percentage or amount
 * (the card then shows "DEAL").
 *
 * Two grid-only guards, both kept from the old title-only rule: the grid prints "OFF" after an amount, so an amount is
 * shown only when it is a stored FIXED value, the title's "$" amount (old rule), or an amount the description states as a
 * discount ("$20 off", "save $20"), never a price read from the description ("orders over $35", "then $0.01 per
 * contact"); and a stored type DEAL still reads its figure from the text, as before.
 */
export function getDiscountDisplay(
    title: string,
    discountType?: OfferDiscountType,
    discountValue?: number | null,
    description?: string | null,
): OfferDiscountDisplay | null {
    const badge = getCouponBadge({ title, description, discountType: discountType === "DEAL" ? null : discountType, discountValue });
    if (badge.type === "percent") {
        // Defensive: a percent badge without a number never renders; the grid shows "DEAL" then.
        return /^\d/.test(badge.value) ? { value: badge.value.replace("%", ""), suffix: "OFF %", isPercent: true } : null;
    }
    if (badge.type === "money") {
        const storedFixed = discountType === "FIXED" && discountValue != null && discountValue > 0;
        const shown = parseMoney(badge.value);
        // The title's own amount, also in the description's spelling ("$7 Off" over "$7.20", "$5" over "€5").
        const fromTitle = shown !== null && parseMoneyAll(title).some(m => m.currency === "$" && (m.value === shown.value || m.value === Math.round(shown.value)));
        const discountInText = parseDiscountAmounts(description).map(formatMoney).includes(badge.value);
        return storedFixed || fromTitle || discountInText ? { value: badge.value, suffix: "OFF", isPercent: false } : null;
    }
    if (badge.value === "FREE SHIP") return { value: "FREE", suffix: "SHIP", isPercent: false };
    return null;
}
